import { AxiosPromise, AxiosRequestConfig } from 'axios';
import { saveAs } from 'file-saver';

import {
  BaseRepository,
  IEnumerableResponse,
  ITableRequestParams
} from '@app/src/common/repository/BaseRepository';
import {
  IEvent,
  IEventForUpdate,
  IEventInList,
  IVisitType
} from '@app/src/components/event-management/model/Event';
import {
  CheckInType,
  ICheckInResult,
  ICheckOutResult,
  IEventVisitor,
  IEventVisitorProperties,
  IVisitor,
  IVisitorBadgeResponse,
  IVisitorDocument,
  IVisitorDocumentHistory,
  IVisitorFormObject,
  IVisitorListItem,
  IVisitorSearchResponse,
  IVisitorType,
  IMatrixCheckInCardResult,
  ISiportCheckInResult,
  ISiportCheckOutCardResult,
  VisitorSearchCodeType
} from '@app/src/components/event-management/model/Visitor';
import { INotificationRules } from '../model/EventNotification';
import { IRetentionPolicySettings } from '../model/RetentionPolicySettings';
import {
  EvacuationListFormat,
  IEvacuationListSettings,
  IEventSettings,
  IPlatformSettings
} from '@app/src/components/event-management/model/CheckInSettings';
import {
  ITemplateLanguages,
  ITemplateSettings
} from '@app/src/components/event-management/model/EventTemplate';
import { IVisit } from '../model/VisitHistory';
import { IVisitorCompany } from '../model/VisitorCompany';
import { getDownloadedFileNameWithRestrictedMessage } from '@app/src/common/helpers/getHighRestrictedDataMessage';
import {
  IDashboardIdsItem,
  IDashboardRecord,
  IDashboardSendInvitationItemDto,
  IDashboardStatisticsDto,
  IDashboardStatisticsRequestParams
} from '@app/src/components/event-management/model/Dashboard';
import localeStore from '@app/src/common/store/LocaleStore';
import { ISyncSettings } from '../model/CheckInSyncSettings';
import {
  ICreateEventVisitorParkingBooking,
  IEventVisitorParkingBooking,
  IUpdateEventVisitorParkingBooking
} from '../model/EventParkingBooking';

export interface IEventTableRequestParams extends ITableRequestParams {
  includeVisitors?: boolean;
  includeEventAssets?: boolean;
}

export interface IVisitorDashboardTableRequestParams extends ITableRequestParams {
  timeZone: string;
}

export interface IVisitorTableRequestParams extends ITableRequestParams {
  includeAssets?: boolean;
  selfRegistered?: boolean;
  excludeBlocked?: boolean;
  fillCardsIds?: boolean;
  timeZone?: string;
}

export interface IVisitorSearchParams {
  codeType: VisitorSearchCodeType;
  code: string;
}

export interface IVisitorCheckInParams {
  visitorId: string;
  cardId?: string;
}

export interface IDashboardVisitorCheckInItemDto {
  eventId: string;
  visitorData?: IVisitorCheckInParams[];
}

export interface IDashboardCheckOutItemDto {
  eventId: string;
  visitorIds?: string[];
  visitorData?: IVisitorCheckInParams[];
}

export interface IGetBadgeParams {
  type: number;
  language: string;
  manual: boolean;
  timeZone: string;
  badgeId: string;
  eventId?: string;
  visitorId?: string;
  code?: string;
  mustBeCheckedIn?: boolean;
}

export interface IVisitorDocumentsParams {
  startIndex: number;
  endIndex: number;
  latest: boolean;
  search?: string;
}

export interface IEvacuationListParams {
  culture: string;
  timeZone: string;
  locations?: string[];
  format?: EvacuationListFormat;
}

class EventsRepository extends BaseRepository {
  eventsUrl = `${this.ereceptionstUrl}/events`;

  public getEvents(
    params?: IEventTableRequestParams
  ): AxiosPromise<IEnumerableResponse<IEventInList>> {
    return this.callGet<IEnumerableResponse<IEventInList>, ITableRequestParams>(
      this.eventsUrl,
      params
    );
  }

  public getEventIds(params?: ITableRequestParams): Promise<string[]> {
    return this.callGet<string[], ITableRequestParams>(
      `${this.eventsUrl}/ids`,
      params
    ).then(response => response.data);
  }

  public getEvent(eventId: string): Promise<IEvent> {
    return this.callGet<IEvent>(`${this.eventsUrl}/${eventId}`).then(
      response => response.data
    );
  }

  public createEvent(event: IEventForUpdate): AxiosPromise<string> {
    return this.callPost<string>(this.eventsUrl, event);
  }

  public updateEvent(event: IEventForUpdate): AxiosPromise<void> {
    return this.callPut<void>(`${this.eventsUrl}/${event.eventId}`, event);
  }

  public deleteEvent(id: string): AxiosPromise<void> {
    return this.callDelete(`${this.eventsUrl}/${id}`);
  }

  public deleteBulkEvent(ids: string[]): AxiosPromise<void> {
    return this.callDelete(`${this.eventsUrl}/ids`, { ids });
  }

  public uploadEvents(fileId: string): AxiosPromise<string> {
    return this.callPost(`${this.eventsUrl}/upload`, { fileId });
  }

  public downloadEvents(params?: IEventTableRequestParams): Promise<any> {
    const url = `${this.eventsUrl}/download`;
    return this.callGet<any>(url, params).then(response => {
      const blob = new Blob([response.data], {
        type: 'text/plain;charset=utf-8'
      });
      const title = `${localeStore.formatMessage('event.table.toolbar.download.events.filename')}.csv`;
      const downloadedFileName =
        getDownloadedFileNameWithRestrictedMessage(title);
      saveAs(blob, downloadedFileName);
    });
  }

  public getEventVisitors(
    eventId: string,
    params?: {}
  ): AxiosPromise<IEnumerableResponse<IEventVisitor>> {
    return this.callGet<IEnumerableResponse<IEventVisitor>, {}>(
      `${this.eventsUrl}/${eventId}/visitors`,
      params
    );
  }

  public getEventVisitorProperties(
    eventId: string,
    visitorId: string
  ): Promise<IEventVisitorProperties> {
    return this.callGet<IEventVisitorProperties>(
      `${this.eventsUrl}/${eventId}/visitors/${visitorId}`
    ).then(r => r.data);
  }

  public updateEventVisitorProperties(
    eventId: string,
    visitorId: string,
    data: IEventVisitorProperties
  ): Promise<void> {
    return this.callPut<void>(
      `${this.eventsUrl}/${eventId}/visitors/${visitorId}`,
      data
    ).then(r => r.data);
  }

  public getEventVisitorsIds(
    eventId: string,
    params?: IVisitorTableRequestParams
  ): Promise<string[]> {
    return this.callGet<string[], ITableRequestParams>(
      `${this.eventsUrl}/${eventId}/visitors/ids`,
      params
    ).then(response => response.data);
  }

  public createVisitor(visitor: IVisitorFormObject): Promise<string> {
    return this.callPost<string>(`${this.ereceptionstUrl}/visitors`, {
      ...visitor
    }).then(response => response.data);
  }

  public assignVisitorsToEvent(
    eventId: string,
    visitorIds: string[]
  ): AxiosPromise<void> {
    return this.callPost(`${this.eventsUrl}/${eventId}/visitors`, {
      visitorsIds: visitorIds
    });
  }

  public getVisitor(visitorId: string, params?: {}): AxiosPromise<IVisitor> {
    return this.callGet<IVisitor, {}>(
      `${this.ereceptionstUrl}/visitors/${visitorId}`,
      params
    );
  }

  public getVisitorDocuments(
    visitorId: string,
    params: IVisitorDocumentsParams
  ): Promise<IEnumerableResponse<IVisitorDocument>> {
    return this.callGet<IEnumerableResponse<IVisitorDocument>>(
      `${this.ereceptionstUrl}/visitors/${visitorId}/documents`,
      params
    ).then(response => response.data);
  }

  public getVisitorDocumentHistory(
    visitorId: string,
    documentId: string
  ): Promise<IEnumerableResponse<IVisitorDocumentHistory>> {
    return this.callGet<IEnumerableResponse<IVisitorDocumentHistory>>(
      `${this.ereceptionstUrl}/visitors/${visitorId}/documents/${documentId}/history`
    ).then(response => response.data);
  }

  public updateVisitor(visitor: IVisitorFormObject): AxiosPromise<void> {
    return this.callPut(
      `${this.ereceptionstUrl}/visitors/${visitor.id}`,
      visitor
    );
  }

  public getVisitors(
    params?: IVisitorTableRequestParams
  ): AxiosPromise<IEnumerableResponse<IVisitorListItem>> {
    return this.callGet<
      IEnumerableResponse<IVisitorListItem>,
      IVisitorTableRequestParams
    >(`${this.ereceptionstUrl}/visitors`, params);
  }

  public getVisitorsIds(
    params?: IVisitorTableRequestParams
  ): Promise<string[]> {
    return this.callGet<string[], ITableRequestParams>(
      `${this.ereceptionstUrl}/visitors/ids`,
      params
    ).then(response => response.data);
  }

  public deleteVisitor(visitorId?: string): AxiosPromise<void> {
    const deleteCondition = visitorId ? `/${visitorId}` : '';
    return this.callDelete(
      `${this.ereceptionstUrl}/visitors${deleteCondition}`
    );
  }

  public deleteBulkVisitors(ids: string[]): AxiosPromise<void> {
    return this.callDelete(`${this.ereceptionstUrl}/visitors/ids`, { ids });
  }

  public uploadVisitors(fileId: string): AxiosPromise<string> {
    return this.callPost(
      `${this.ereceptionstUrl}/visitors/file/${fileId}/upload`
    );
  }

  public syncExternalVisitors(): AxiosPromise<void> {
    return this.callPost<void>(
      `${this.ereceptionstUrl}/visitors/import-from-external`
    );
  }

  public deleteEventVisitor(eventId: string, visitorId?: string): AxiosPromise {
    const deleteCondition = visitorId ? `/${visitorId}` : '';
    return this.callDelete(
      `${this.eventsUrl}/${eventId}/visitors${deleteCondition}`
    );
  }

  public uploadVisitorsForEvent(
    eventId: string,
    fileId: string
  ): AxiosPromise<string> {
    return this.callPost(`${this.eventsUrl}/${eventId}/visitors/upload`, {
      fileId
    });
  }

  public getVisitorCompanies(
    params?: ITableRequestParams
  ): Promise<IEnumerableResponse<IVisitorCompany>> {
    return this.callGet<
      IEnumerableResponse<IVisitorCompany>,
      ITableRequestParams
    >(`${this.ereceptionstUrl}/visitorCompanies`, params).then(
      response => response.data
    );
  }

  public getVisitorCompanyIds(params?: ITableRequestParams): Promise<string[]> {
    return this.callGet<string[], ITableRequestParams>(
      `${this.ereceptionstUrl}/visitorCompanies/ids`,
      params
    ).then(response => response.data);
  }

  public deleteVisitorCompany(visitorCompanyId?: string): AxiosPromise<void> {
    const deleteCondition = visitorCompanyId ? `/${visitorCompanyId}` : '';
    return this.callDelete(
      `${this.ereceptionstUrl}/visitorCompanies${deleteCondition}`
    );
  }

  public deleteBulkCompanies(ids: string[]): AxiosPromise<void> {
    return this.callDelete(`${this.ereceptionstUrl}/visitorCompanies/ids`, {
      ids
    });
  }

  public uploadVisitorCompanies(fileId: string): AxiosPromise<string> {
    return this.callPost(`${this.ereceptionstUrl}/visitorCompanies/upload`, {
      fileId
    });
  }

  public blockVisitorCompany(visitorCompanyId: string) {
    return this.callPut(
      `${this.ereceptionstUrl}/visitorCompanies/${visitorCompanyId}/block`,
      {}
    );
  }

  public unblockVisitorCompany(visitorCompanyId: string) {
    return this.callPut(
      `${this.ereceptionstUrl}/visitorCompanies/${visitorCompanyId}/unblock`,
      {}
    );
  }

  public getVisitorCompany(
    visitorCompanyId: string
  ): AxiosPromise<IVisitorCompany> {
    return this.callGet<IVisitorCompany>(
      `${this.ereceptionstUrl}/visitorCompanies/${visitorCompanyId}`
    );
  }

  public createVisitorCompany(
    visitorCompany: IVisitorCompany
  ): Promise<string> {
    return this.callPost<string>(
      `${this.ereceptionstUrl}/visitorCompanies`,
      visitorCompany
    ).then(response => response.data);
  }

  public downloadVisitorCompanies(params?: ITableRequestParams) {
    const url = `${this.ereceptionstUrl}/visitorCompanies/download`;
    return this.callGet<ArrayBuffer>(url, params, {
      responseType: 'arraybuffer'
    }).then(result => result.data);
  }

  public updateVisitorCompany(
    visitorCompany: IVisitorCompany
  ): AxiosPromise<void> {
    return this.callPut(
      `${this.ereceptionstUrl}/visitorCompanies/${visitorCompany.id}`,
      visitorCompany
    );
  }

  public sendInvitations(
    eventId: string,
    visitorIds: string[],
    groupInvitation: boolean
  ): AxiosPromise {
    return this.callPost(`${this.eventsUrl}/send-invitation`, {
      eventId,
      visitorIds,
      groupInvitation
    });
  }

  public sendInvitationsDashboard(
    items: IDashboardSendInvitationItemDto[]
  ): AxiosPromise {
    return this.callPost(`${this.eventsUrl}/send-invitation-bulk`, items);
  }

  public downloadVisitors(
    fileName: string,
    eventId?: string,
    params?: IVisitorTableRequestParams
  ): Promise<any> {
    const url = eventId
      ? `${this.eventsUrl}/${eventId}/visitors/download`
      : `${this.ereceptionstUrl}/visitors/download`;
    return this.callGet<any>(url, params).then(response => {
      const blob = new Blob([response.data], {
        type: 'text/plain;charset=utf-8'
      });
      const downloadedFileName =
        getDownloadedFileNameWithRestrictedMessage(fileName);
      saveAs(blob, downloadedFileName);
    });
  }

  public downloadEvacuationList(params: IEvacuationListParams) {
    const url = `${this.ereceptionstUrl}/visitors/evacuation-list/download`;
    return this.callGet<ArrayBuffer>(url, params, {
      responseType: 'arraybuffer'
    }).then(result => result.data);
  }

  public downloadImportTemplate(language: string): Promise<any> {
    return this.callGet<any>(
      `${this.ereceptionstUrl}/visitors/download-import-template`,
      {
        language
      },
      {
        responseType: 'blob'
      }
    );
  }

  public checkInVisitors(
    eventId: string,
    visitors: IVisitorCheckInParams[]
  ): Promise<ICheckInResult[]> {
    return this.callPost<ICheckInResult[]>(
      `${this.eventsUrl}/${eventId}/visitors/check-in`,
      visitors
    ).then(result => result.data);
  }

  public checkInDashboardVisitors(
    visitors: IDashboardVisitorCheckInItemDto[]
  ): Promise<ICheckInResult[]> {
    return this.callPost<ICheckInResult[]>(
      `${this.eventsUrl}/visitors/check-in-bulk`,
      visitors
    ).then(result => result.data);
  }

  public siportCheckInWithPhysicalCard(
    checkInId: string,
    cardId: string
  ): Promise<ISiportCheckInResult> {
    return this.callPost<ISiportCheckInResult>(
      `${this.ereceptionstUrl}/siport-integration/check-in/physical-card`,
      { checkInId, cardId }
    ).then(result => result.data);
  }

  public siportCheckInWithQr(checkInId: string): Promise<ISiportCheckInResult> {
    return this.callPost<ISiportCheckInResult>(
      `${this.ereceptionstUrl}/siport-integration/check-in/qr`,
      { checkInId }
    ).then(result => result.data);
  }

  public registerCheckIn(
    eventId: string,
    visitorId: string
  ): Promise<ICheckInResult> {
    return this.callPost<ICheckInResult>(`${this.eventsUrl}/checkIn`, {
      eventId,
      visitorId,
      type: CheckInType.EventIdVisitorId,
      registrationOnly: true
    }).then(result => result.data);
  }

  public matrixCheckInWithPhysicalCard(
    checkInId: string,
    cardId: string
  ): Promise<IMatrixCheckInCardResult> {
    return this.callPost<IMatrixCheckInCardResult>(
      `${this.ereceptionstUrl}/matrix-integration/check-in/physical-card`,
      { checkInId, cardId }
    ).then(result => result.data);
  }

  public siportCheckOutPhysicalCard(
    cardId: string
  ): Promise<ISiportCheckOutCardResult> {
    return this.callPost<ISiportCheckOutCardResult>(
      `${this.ereceptionstUrl}/siport-integration/check-out/physical-card`,
      { cardId }
    ).then(result => result.data);
  }

  public checkOutVisitors(
    eventId: string,
    visitors: IVisitorCheckInParams[]
  ): Promise<ICheckOutResult[]> {
    return this.callPost<ICheckOutResult[]>(
      `${this.eventsUrl}/${eventId}/visitors/check-out`,
      visitors
    ).then(result => result.data);
  }

  public checkOutDashboardVisitors(
    data: IDashboardCheckOutItemDto[]
  ): Promise<ICheckOutResult[]> {
    return this.callPost<ICheckOutResult[]>(
      `${this.eventsUrl}/visitors/check-out-bulk`,
      data
    ).then(result => result.data);
  }

  public blockVisitor(visitorId: string) {
    return this.callPut(
      `${this.ereceptionstUrl}/visitors/${visitorId}/block`,
      {}
    );
  }

  public unblockVisitor(visitorId: string) {
    return this.callPut(
      `${this.ereceptionstUrl}/visitors/${visitorId}/unblock`,
      {}
    );
  }

  public getVisitorsBadge(params: IGetBadgeParams, config?: any) {
    return this.callGet<IVisitorBadgeResponse>(
      `${this.ereceptionstUrl}/Visitors/badge`,
      params,
      config
    ).then(result => result.data);
  }

  /* Event Settings */
  public getEventCreationSettings(): AxiosPromise<IEventSettings> {
    return this.callGet<IEventSettings>(
      `${this.ereceptionstUrl}/settings/event-settings`
    );
  }

  public updateEventCreationSettings(settings: IEventSettings) {
    return this.callPut(
      `${this.ereceptionstUrl}/settings/event-settings`,
      settings
    );
  }

  public getEventTemplateSettings(): AxiosPromise<ITemplateSettings> {
    return this.callGet<ITemplateSettings>(
      `${this.ereceptionstUrl}/settings/template-settings`
    );
  }

  public getEventTemplateLanguages(): Promise<ITemplateLanguages> {
    return this.callGet<ITemplateLanguages>(
      `${this.ereceptionstUrl}/settings/template-settings/languages`
    ).then(response => response.data);
  }

  public updateEventTemplateSettings(settings: ITemplateSettings) {
    return this.callPut(
      `${this.ereceptionstUrl}/settings/template-settings`,
      settings
    );
  }

  public getPlatformSettings() {
    return this.callGet<IPlatformSettings>(
      `${this.ereceptionstUrl}/settings/event-settings/platform`
    );
  }

  public getNotificationRules() {
    return this.callGet<INotificationRules>(
      `${this.ereceptionstUrl}/settings/notification-rules`
    );
  }

  public updateNotificationRules(rules: INotificationRules) {
    return this.callPut(
      `${this.ereceptionstUrl}/settings/notification-rules`,
      rules
    );
  }

  public getEvacuationListSettings() {
    return this.callGet<IEvacuationListSettings>(
      `${this.ereceptionstUrl}/settings/evacuation-list`
    );
  }

  public updateEvacuationListSettings(settings: IEvacuationListSettings) {
    return this.callPut(
      `${this.ereceptionstUrl}/settings/evacuation-list`,
      settings
    );
  }

  public getRetentionPolicySettings(): Promise<IRetentionPolicySettings> {
    return this.callGet<IRetentionPolicySettings>(
      `${this.ereceptionstUrl}/settings/event-settings/retention-policy`
    ).then(r => r.data);
  }

  public updateRetentionPolicySettings(
    settings: IRetentionPolicySettings
  ): Promise<IRetentionPolicySettings> {
    return this.callPut<IRetentionPolicySettings>(
      `${this.ereceptionstUrl}/settings/event-settings/retention-policy`,
      settings
    ).then(r => r.data);
  }

  public getSyncSettings() {
    return this.callGet<ISyncSettings>(
      `${this.ereceptionstUrl}/settings/sync-settings`
    );
  }

  public updateSyncSettings(settings: ISyncSettings) {
    return this.callPut(
      `${this.ereceptionstUrl}/settings/sync-settings`,
      settings
    );
  }

  /* Visit History */
  public getVisitHistory(
    params?: ITableRequestParams,
    config?: AxiosRequestConfig
  ): Promise<IEnumerableResponse<IVisit>> {
    return this.callGet<IEnumerableResponse<IVisit>, ITableRequestParams>(
      `${this.ereceptionstUrl}/visitors/history`,
      params,
      config
    ).then(result => result.data);
  }

  public deleteVisitHistory(id: string): AxiosPromise<void> {
    return this.callDelete(`${this.ereceptionstUrl}/visitors/history/${id}`);
  }

  public downloadVisitHistory(params?: ITableRequestParams) {
    return this.callGet<ArrayBuffer>(
      `${this.ereceptionstUrl}/visitors/history/download`,
      params,
      { responseType: 'arraybuffer' }
    ).then(result => result.data);
  }

  public getLatestEventVisitor(params: IVisitorSearchParams) {
    return this.callGet<IVisitorSearchResponse>(
      `${this.eventsUrl}/latest-event-visitor`,
      params
    );
  }

  /* Visit type */
  public getVisitTypes(params?: any) {
    return this.callGet<IEnumerableResponse<IVisitType>>(
      `${this.ereceptionstUrl}/visit-types`,
      params
    ).then(result => result.data);
  }

  // the list endpoint always returns preApproval as null, it is only filled in per type
  public getVisitType(id: string): Promise<IVisitType> {
    return this.callGet<IVisitType>(
      `${this.ereceptionstUrl}/visit-types/${id}`
    ).then(result => result.data);
  }

  public createVisitType(type: IVisitType): AxiosPromise<string> {
    return this.callPost<string>(`${this.ereceptionstUrl}/visit-types`, type);
  }

  public updateVisitType(type: IVisitType) {
    return this.callPut<void>(
      `${this.ereceptionstUrl}/visit-types/${type.id}`,
      type
    );
  }

  /* Visitor type */
  public getVisitorTypes() {
    return this.callGet<IEnumerableResponse<IVisitorType>>(
      `${this.ereceptionstUrl}/visitor-types`
    ).then(result => result.data);
  }

  /* Dashboard */
  public async getVisitorDashboardData(
    params?: IVisitorDashboardTableRequestParams
  ) {
    return this.callGet<IEnumerableResponse<IDashboardRecord>>(
      `${this.ereceptionstUrl}/dashboard`,
      params,
      {}
    ).then(result => result.data);
  }

  public async getVisitorDashboardStatistics(
    params?: IDashboardStatisticsRequestParams
  ) {
    return this.callGet<IDashboardStatisticsDto>(
      `${this.ereceptionstUrl}/dashboard/statistics`,
      params
    ).then(result => result.data);
  }

  public async getVisitorDashboardIds(
    params?: IVisitorDashboardTableRequestParams
  ) {
    return this.callGet<IDashboardIdsItem[]>(
      `${this.ereceptionstUrl}/dashboard/ids`,
      params
    ).then(result => result.data);
  }

  public downloadDashboardVisitors(
    params?: IVisitorDashboardTableRequestParams
  ): Promise<any> {
    const url = `${this.ereceptionstUrl}/dashboard/download`;
    return this.callGet<any>(url, params).then(response => {
      const blob = new Blob([response.data], {
        type: 'text/plain;charset=utf-8'
      });
      const title = `${localeStore.formatMessage('visitor.list.toolbar.downloaded.visitors.file.name')}.csv`;
      const downloadedFileName =
        getDownloadedFileNameWithRestrictedMessage(title);
      saveAs(blob, downloadedFileName);
    });
  }

  /* Event parking bookings */

  public getEventVisitorParkingBookings(eventId: string, visitorId: string) {
    return this.callGet<IEventVisitorParkingBooking[]>(
      `${this.eventsUrl}/${eventId}/visitors/${visitorId}/parking-bookings`
    ).then(result => result.data);
  }

  public createEventVisitorParkingBookings(
    eventId: string,
    visitorId: string,
    booking: ICreateEventVisitorParkingBooking
  ) {
    return this.callPost<string>(
      `${this.eventsUrl}/${eventId}/visitors/${visitorId}/parking-bookings`,
      booking
    ).then(result => result.data);
  }

  public createEventParkingBooking(
    eventId: string,
    booking: ICreateEventVisitorParkingBooking
  ) {
    return this.callPost<string>(
      `${this.eventsUrl}/${eventId}/parking-bookings`,
      booking
    ).then(result => result.data);
  }

  public linkEventVisitorParkingBookings(
    eventId: string,
    visitorId: string,
    ids: string[]
  ) {
    return this.callPost(
      `${this.eventsUrl}/${eventId}/visitors/${visitorId}/parking-bookings/link`,
      { ids }
    ).then(result => result.data);
  }

  public updateEventVisitorParkingBooking(
    eventId: string,
    visitorId: string,
    bookingId: string,
    booking: IUpdateEventVisitorParkingBooking
  ) {
    return this.callPut(
      `${this.eventsUrl}/${eventId}/visitors/${visitorId}/parking-bookings/${bookingId}`,
      booking
    ).then(result => result.data);
  }
}

export default new EventsRepository();
